import { useCallback, useEffect, useState } from "react";
import { UserPlus, X } from "lucide-react";

import {
  createInvite,
  getMe,
  listInvites,
  listMembers,
  revokeInvite,
  setMemberRole,
  type Invite,
  type Member,
} from "@/lib/api";
import { humanizeError } from "@/lib/errors";
import { useZodForm, z, emailSchema } from "@/lib/validation";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ui/field";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeletonRows,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/toast";
import { useConfirm } from "@/components/ui/confirm";
import { Empty, ErrorState, PageHeader, Spinner } from "@/components/Page";

// Org roles. "member" is the legacy alias for viewer; it's displayed as
// "viewer" and never offered as a new selection.
const ORG_ROLES = ["viewer", "editor", "admin", "owner"] as const;
const ROLE_CLS: Record<string, string> = {
  owner: "border-primary/30 bg-primary/10 text-primary",
  admin: "border-success/30 bg-success/10 text-success",
  editor: "border-warning/40 bg-warning/10 text-warning",
  viewer: "border-border bg-muted text-muted-foreground",
  member: "border-border bg-muted text-muted-foreground",
};
const roleLabel = (r: string) => (r === "member" ? "viewer" : r);

function RoleBadge({ role }: { role: string }) {
  return (
    <Badge variant="outline" className={cn("capitalize", ROLE_CLS[role] ?? ROLE_CLS.viewer)}>
      {roleLabel(role)}
    </Badge>
  );
}

export function Members() {
  const [members, setMembers] = useState<Member[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [isOwner, setIsOwner] = useState(false);
  const [meId, setMeId] = useState<string>("");
  const [savingRole, setSavingRole] = useState<string | null>(null);

  const toast = useToast();
  const confirm = useConfirm();

  const load = useCallback(() => {
    setLoading(true);
    setLoadErr(null);
    return Promise.all([listMembers(), listInvites(), getMe()])
      .then(([m, all, me]) => {
        setMembers(m);
        setInvites(all.filter((i) => !i.accepted));
        setMeId(me.user_id ?? "");
        // API keys are owner-equivalent; a session user is an owner only if
        // their role in the current workspace is owner.
        const wsRole = me.workspaces?.find((w) => w.tenant_id === me.current_tenant_id)?.role;
        setIsOwner(me.auth === "api_key" || wsRole === "owner");
      })
      .catch((e) => setLoadErr(humanizeError(e, "Couldn't load members")))
      .finally(() => setLoading(false));
  }, []);

  const changeRole = useCallback(
    async (m: Member, role: string) => {
      setSavingRole(m.user_id);
      try {
        await setMemberRole(m.user_id, role);
        setMembers((cur) => cur.map((x) => (x.user_id === m.user_id ? { ...x, role } : x)));
        toast.success(`${m.email ?? m.user_id} is now ${role}.`);
      } catch (e) {
        toast.error(humanizeError(e, "Couldn't change the role"));
      } finally {
        setSavingRole(null);
      }
    },
    [toast],
  );

  useEffect(() => {
    void load();
  }, [load]);

  async function revoke(i: Invite) {
    const ok = await confirm({
      title: "Revoke this invite?",
      description: `The invite link sent to ${i.email} will stop working.`,
      confirmText: "Revoke invite",
      destructive: true,
    });
    if (!ok) return;
    setRevokingId(i.id);
    try {
      await revokeInvite(i.id);
      toast.success("Invite revoked");
      await load();
    } catch (e) {
      toast.error(humanizeError(e, "Couldn't revoke the invite"));
    } finally {
      setRevokingId(null);
    }
  }

  return (
    <div>
      <PageHeader
        title="Members"
        description="People in this workspace. Owners can invite teammates and manage keys."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button>
                <UserPlus /> Invite
              </Button>
            </DialogTrigger>
            <InviteDialog
              onDone={() => {
                setOpen(false);
                toast.success("Invite sent");
                void load();
              }}
            />
          </Dialog>
        }
      />

      {loadErr ? (
        <ErrorState message={loadErr} onRetry={load} />
      ) : (
        <div className="space-y-8">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableSkeletonRows cols={2} />
              ) : (
                members.map((m) => {
                  // Owners can change roles, except their own (no self-lockout);
                  // the workspace owner is also protected server-side.
                  const editable = isOwner && m.user_id !== meId;
                  return (
                    <TableRow key={m.user_id}>
                      <TableCell className="font-medium">{m.email ?? m.user_id}</TableCell>
                      <TableCell>
                        {editable ? (
                          <div className="flex items-center gap-2">
                            <select
                              value={roleLabel(m.role)}
                              disabled={savingRole === m.user_id}
                              onChange={(e) => void changeRole(m, e.target.value)}
                              className="h-8 rounded-md border border-input bg-background px-2 text-xs capitalize shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 disabled:opacity-50"
                            >
                              {ORG_ROLES.map((r) => (
                                <option key={r} value={r}>
                                  {r}
                                </option>
                              ))}
                            </select>
                            {savingRole === m.user_id && <Spinner className="h-3.5 w-3.5" />}
                          </div>
                        ) : (
                          <RoleBadge role={m.role} />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          {!loading && (
            <div>
              <h2 className="mb-2 text-sm font-medium text-muted-foreground">Pending invites</h2>
              {invites.length === 0 ? (
                <Empty>No pending invites.</Empty>
              ) : (
                <Table>
                  <TableBody>
                    {invites.map((i) => (
                      <TableRow key={i.id}>
                        <TableCell className="font-medium">{i.email}</TableCell>
                        <TableCell>
                          <RoleBadge role={i.role} />
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            disabled={revokingId === i.id}
                            aria-label="Revoke invite"
                            onClick={() => void revoke(i)}
                          >
                            {revokingId === i.id ? <Spinner className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const inviteSchema = z.object({
  email: emailSchema,
  role: z.enum(["viewer", "editor", "admin", "owner"]),
});
type InviteValues = z.infer<typeof inviteSchema>;

function InviteDialog({ onDone }: { onDone: () => void }) {
  const [err, setErr] = useState("");
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useZodForm(inviteSchema, { defaultValues: { email: "", role: "viewer" } });

  async function onSubmit(values: InviteValues) {
    setErr("");
    try {
      await createInvite(values.email, values.role);
      onDone();
    } catch (e) {
      setErr(humanizeError(e, "Couldn't send the invite"));
    }
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Invite a teammate</DialogTitle>
        <DialogDescription>They'll get an email link to join this workspace.</DialogDescription>
      </DialogHeader>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3" noValidate>
        <Field label="Email" htmlFor="ie" error={errors.email?.message}>
          <Input id="ie" type="email" placeholder="teammate@company.com" autoFocus aria-invalid={!!errors.email} {...register("email")} />
        </Field>
        <Field label="Role" htmlFor="ir">
          <select
            id="ir"
            {...register("role")}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm capitalize shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
          >
            <option value="viewer">Viewer — read-only</option>
            <option value="editor">Editor — deploy + manage tools</option>
            <option value="admin">Admin — + domains &amp; access</option>
            <option value="owner">Owner — full control</option>
          </select>
        </Field>
        {err && <p className="text-sm text-destructive">{err}</p>}
        <DialogFooter>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Spinner />}
            {isSubmitting ? "Sending…" : "Send invite"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
